import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { TableContainer } from "../../style/contents/backup/backup";
import { DownloadFileItem } from "../../view/contents/download/download.list";
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  PaginationState,
  Row,
  useReactTable,
} from "@tanstack/react-table";
import Pagination from "../common/table.pagination";
import { formatDate } from "../../util/util";
import {
  fetchDeleteDownloadFileList,
  fetchDownloadFile,
} from "../../api/admin";

interface Props {
  data: DownloadFileItem[];
  pageSize: number;
  userId: string;
  deviceId: string;
  isLoading?: boolean; // ✅ 추가
  isFetched?: boolean; // ✅ 추가
}

const DownloadList = ({
  data,
  pageSize,
  userId,
  deviceId,
  isLoading,
  isFetched,
}: Props) => {
  console.log("props", data);
  const { register, handleSubmit } = useForm();
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize,
  });
  const [rowSelection, setRowSelection] = useState<Record<string, boolean>>({});

  // ✅ 날짜 기준 정렬된 데이터 생성
  const sortedData = useMemo(() => {
    return [...data].sort((a, b) => {
      const dateA = new Date(a.createdDate).getTime();
      const dateB = new Date(b.createdDate).getTime();
      return dateB - dateA; // 최신순 (내림차순)
    });
  }, [data]);

  const columns = useMemo<ColumnDef<DownloadFileItem>[]>(
    () => [
      // {
      //   header: "No.",
      //   id: "number", // 넘버링을 위한 id
      //   // cell: ({ row }) => row.index + 1, // 각 행의 인덱스에 1을 더해 넘버링
      //   cell: ({ row }: { row: Row<DownloadFileItem> }) => {
      //     const totalCount = data.length; // totalRowCount = 전체 데이터 수
      //     if (totalCount === 0) return "-";

      //     const globalIndex = row.index; // 이미 글로벌 인덱스임
      //     return totalCount - globalIndex;
      //   },
      //   size: 60,
      // },
      {
        header: "아이디", // userAccount로 변경 예정
        accessorKey: "userAccount",
        cell: (info) => info.getValue(),
        size: 150,
      },
      {
        header: "디바이스",
        accessorKey: "deviceId",
        cell: (info) => info.getValue(),
        size: 120,
      },
      {
        header: "압축 파일 이름",
        accessorKey: "name",
        cell: (info) => info.getValue(),
        size: 350,
      },
      {
        header: "실제경로",
        accessorKey: "targetPath",
        cell: (info) => info.getValue(),
        size: 350,
      },
      {
        header: "상태",
        accessorKey: "state",
        cell: (info) => {
          const type = info.getValue();
          if (type === "completed") return "완료";
          if (type === "buffering") return "진행 중";
          return "-";
        },
        size: 100,
      },
      {
        header: "압축 파일 생성 날짜",
        accessorKey: "createdDate",
        cell: (info) => formatDate(info.getValue() as string),
        size: 200,
      },
      {
        header: "다운로드",
        id: "download",
        cell: ({ row }: { row: Row<DownloadFileItem> }) => (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation(); // 행 클릭 이벤트 방지
              handleDownload(row.original); // ✅ 다운로드 핸들러 호출
            }}
            className="download-btn"
          >
            다운로드
          </button>
        ),
        size: 100,
      },
    ],
    [register, data, pagination],
  );
  const table = useReactTable({
    data: sortedData,
    columns,
    state: {
      rowSelection,
      pagination,
    },
    enableRowSelection: true,
    onRowSelectionChange: setRowSelection,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onPaginationChange: setPagination,
  });

  const selectedRowCount = table.getSelectedRowModel().rows.length;
  const totalRowCount = table.getRowCount();

  const onSubmit = async (data: any) => {};

  const handleDownload = async (item: DownloadFileItem) => {
    try {
      const adminUserId = localStorage.getItem("userId") || "";
      const targetUserId = "6";
      const targetDeviceId = item.deviceId || deviceId;

      if (!adminUserId || !targetUserId || !targetDeviceId) {
        alert(
          `필수 정보가 누락되었습니다. userid :${targetUserId}, adminUserId: ${adminUserId}, deviceId: ${targetDeviceId}`,
        );
        return;
      }

      console.log("backupId", item.backupId);

      const res = await fetchDownloadFile(
        adminUserId,
        String(item.backupId),
        targetUserId,
        targetDeviceId,
      );

      const presignedUrl = res.message?.downloadUrl?.downloadUrl;

      console.log("🚀 받은 presignedUrl:", presignedUrl);

      if (!presignedUrl) {
        alert("다운로드 링크를 불러올 수 없습니다.");
        return;
      }

      // ✅ presignedUrl에서 실제 파일명 추출
      const urlParts = presignedUrl.split("?");
      const fileNameWithExt = urlParts[0].split("/").pop(); // c0754812-97d6-4bf7-b276-a14bd90eee57.zip

      const customFileName = `speedbackup_download_${fileNameWithExt}`;

      console.log(customFileName);

      // ✅ a 태그 생성 후 클릭으로 다운로드 처리 (fetch 사용 X)
      const link = document.createElement("a");
      link.href = presignedUrl;
      link.target = "_blank"; // 새 창 열기 (선택사항)
      link.rel = "noopener noreferrer";
      link.download = customFileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error("❌ 다운로드 중 오류 발생:", err);
      alert("파일 다운로드에 실패했습니다.");
    }
  };

  const deleteAllList = async () => {
    const adminUserId = localStorage.getItem("userId");
    try {
      const res = await fetchDeleteDownloadFileList(adminUserId ?? "");
      console.log(res);
      alert("모든 다운로드 파일을 삭제했습니다.");
    } catch (err) {
      console.error(err);
    }
  };

  if (isLoading) {
    return (
      <TableContainer>
        <div className="loading">📥 다운로드 목록을 불러오는 중입니다...</div>
      </TableContainer>
    );
  }

  // if (!userId || !deviceId) {
  //   return (
  //     <TableContainer>
  //       <div className="loading">👤 사용자와 디바이스를 선택해주세요.</div>
  //     </TableContainer>
  //   );
  // }

  if (isFetched && data.length === 0) {
    return (
      <TableContainer>
        <div className="no-data">🔍 다운로드 가능한 파일이 없습니다.</div>
      </TableContainer>
    );
  }

  return (
    <TableContainer>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="table-head-wrap">
          <div className="left">
            <span className="table-total-row">총 {totalRowCount}</span>
          </div>
          <div className="btn-box">
            <button type="button" onClick={deleteAllList}>
              리스트 전체 삭제
            </button>
          </div>
        </div>
        {data.length === 0 ? (
          <div className="loading">사용자와 디바이스를 선택해주세요.</div>
        ) : (
          <div className="fixed-height" style={{ maxHeight: "47rem" }}>
            <table>
              <thead>
                {table.getHeaderGroups().map((item) => (
                  <tr key={item.id}>
                    {item.headers.map((header) => (
                      <th
                        key={header.id}
                        style={{ width: `${header.getSize()}px` }}
                      >
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext(),
                            )}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                {table.getRowModel().rows.map((row) => (
                  <tr key={row.id}>
                    {row.getVisibleCells().map((cell) => (
                      <td key={cell.id}>
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {table.getPageCount() > 0 && (
          <Pagination
            page={pagination.pageIndex}
            pages={table.getPageCount()}
            onPageChange={(page) => {
              table.setPageIndex(page);
            }}
          />
        )}
      </form>
    </TableContainer>
  );
};

export default DownloadList;
